import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { NAV } from '@/constants/testIds';
import { BookOpen, LogOut, User as UserIcon } from 'lucide-react';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (p) => location.pathname === p;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLink = (to, label, testId) => (
    <Link
      to={to}
      data-testid={testId}
      className={`text-sm tracking-wide transition-colors ${
        isActive(to) ? 'text-[#1E3A8A] font-semibold' : 'text-slate-600 hover:text-[#1E3A8A]'
      }`}
    >
      {label}
    </Link>
  );

  return (
    <nav className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
      <div className="container-page flex items-center justify-between h-16">
        <Link to="/" data-testid={NAV.brand} className="flex items-center gap-2.5 group">
          <span className="w-8 h-8 rounded-md bg-[#1E3A8A] flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-white" strokeWidth={2.2} />
          </span>
          <span className="font-serif-display text-2xl tracking-tight text-slate-900">
            Scripture <span className="text-[#0F766E]">Souls</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLink('/', 'Directory', NAV.linkHome)}
          {navLink('/flashcards', 'Flashcards', NAV.linkFlashcards)}
          {user && navLink('/bookmarks', 'Bookmarks', NAV.linkBookmarks)}
          {navLink('/about', 'About', NAV.linkAbout)}
          {user && (
            <Link to="/profile" data-testid="nav-link-profile" className={`text-sm tracking-wide transition-colors ${isActive('/profile') ? 'text-[#1E3A8A] font-semibold' : 'text-slate-600 hover:text-[#1E3A8A]'}`}>
              Profile
            </Link>
          )}
          {user?.is_admin && (
            <Link
              to="/admin"
              data-testid="nav-link-admin"
              className={`text-sm tracking-wide transition-colors flex items-center gap-1.5 ${
                isActive('/admin') ? 'text-[#0F766E] font-semibold' : 'text-[#0F766E]/80 hover:text-[#0F766E]'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#0F766E]" />
              Admin
            </Link>
          )}
        </div>

        <div className="flex items-center gap-3">
          {user ? (
            <div className="flex items-center gap-3" data-testid={NAV.userMenu}>
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <span className="w-7 h-7 rounded-full bg-[#DBEAFE] flex items-center justify-center text-[#1E3A8A] text-xs font-semibold">
                  {(user.name || user.email)[0].toUpperCase()}
                </span>
                <span className="hidden sm:inline">{user.name || user.email}</span>
              </div>
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="text-slate-500 hover:text-slate-900 transition-colors p-2"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              data-testid={NAV.linkLogin}
              className="btn-primary text-sm px-4 py-2"
            >
              <UserIcon className="w-4 h-4" /> Sign in
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
}
